import { useEffect, useState } from "react";
import api from "@/lib/api";
import { formatErr } from "@/lib/api";
import { toast } from "sonner";
import { PageHeader, Avatar } from "@/components/Shared";
import { Button } from "@/components/ui/button";
import { useCompany } from "@/config";
import { TreeStructure, UsersThree, Buildings, IdentificationBadge, FilePdf } from "@phosphor-icons/react";
import jsPDF from "jspdf";
import html2canvas from "html2canvas";

const hexToRgb = (hex) => {
  const h = (hex || "#000000").replace("#", "");
  const full = h.length === 3 ? h.split("").map((c) => c + c).join("") : h;
  const n = parseInt(full, 16) || 0;
  return [(n >> 16) & 255, (n >> 8) & 255, n & 255];
};

const loadDataUrl = async (url) => {
  try {
    const res = await fetch(url, { mode: "cors" });
    const blob = await res.blob();
    return await new Promise((resolve, reject) => {
      const fr = new FileReader();
      fr.onload = () => resolve(fr.result);
      fr.onerror = reject;
      fr.readAsDataURL(blob);
    });
  } catch (_) { return null; }
};

function Stat({ label, value, icon: Icon }) {
  return (
    <div className="bg-white border border-[#E5E5E5] p-4 flex items-center gap-3" data-testid={`stat-${label.toLowerCase()}`}>
      <div className="h-10 w-10 bg-[#1F4E8C]/10 grid place-items-center"><Icon size={20} className="text-[#1F4E8C]" /></div>
      <div>
        <div className="font-head text-2xl font-bold">{value}</div>
        <div className="eyebrow text-neutral-500">{label}</div>
      </div>
    </div>
  );
}

export default function OrgHierarchy() {
  const [data, setData] = useState(null);
  const [dragId, setDragId] = useState(null);
  const [overTeam, setOverTeam] = useState(null);
  const [view, setView] = useState("list");
  const company = useCompany();

  const load = () => api.get("/org/hierarchy").then((r) => setData(r.data)).catch((e) => toast.error(formatErr(e)));
  useEffect(() => { load(); }, []);

  const moveEmployee = async (empId, team, deptId) => {
    try {
      await api.patch(`/users/${empId}`, {
        team_id: team.id, department_id: deptId || team.department_id || "",
        reporting_admin_id: team.admin?.id || "",
      });
      toast.success("Employee moved");
      load();
    } catch (e) { toast.error(formatErr(e)); }
  };

  if (!data) return <div className="text-neutral-500">Loading…</div>;
  const c = data.counts || {};
  const canDrag = data.is_super;

  return (
    <div data-testid="org-hierarchy-page">
      <PageHeader overline="Organization" title="Organization Hierarchy"
        subtitle="Company → Departments → Teams → Admins → Employees"
        actions={
          <div className="flex border border-[#E5E5E5]">
            <button onClick={() => setView("list")} data-testid="view-list" className={`px-3 h-10 text-xs font-semibold ${view === "list" ? "bg-[#1F4E8C] text-white" : "bg-white text-[#1F4E8C]"}`}>List</button>
            <button onClick={() => setView("chart")} data-testid="view-chart" className={`px-3 h-10 text-xs font-semibold ${view === "chart" ? "bg-[#1F4E8C] text-white" : "bg-white text-[#1F4E8C]"}`}>Org Chart</button>
          </div>
        } />

      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 mb-6">
        <Stat label="Departments" value={c.departments ?? 0} icon={Buildings} />
        <Stat label="Teams" value={c.teams ?? 0} icon={IdentificationBadge} />
        <Stat label="Admins" value={c.admins ?? 0} icon={UsersThree} />
        <Stat label="Employees" value={c.employees ?? 0} icon={UsersThree} />
      </div>

      {view === "chart" && <OrgChart data={data} company={company} />}      {view === "list" && (<>
      {canDrag && (
        <div className="mb-4 text-xs text-neutral-500 bg-[#F7F7F5] border border-[#E5E5E5] px-3 py-2" data-testid="dnd-hint">
          Tip: drag an employee onto another team to move them. The transfer is logged automatically.
        </div>
      )}

      <div className="space-y-6">
        {data.departments.map((d) => (
          <div key={d.id || d.name} className="bg-white border border-[#E5E5E5]" data-testid={`dept-${d.id || d.name}`}>
            <div className="px-5 py-3 bg-[#1F4E8C] text-white font-head font-semibold flex items-center gap-2">
              <Buildings size={18} /> {d.name}
              <span className="ml-auto text-xs font-normal text-sky-200">{d.teams.length} team(s)</span>
            </div>
            {d.teams.length === 0 ? (
              <div className="px-5 py-4 text-sm text-neutral-500">No teams</div>
            ) : (
              <div className="p-4 grid lg:grid-cols-2 gap-4">
                {d.teams.map((t) => (
                  <div key={t.id}
                    onDragOver={canDrag ? (e) => { e.preventDefault(); setOverTeam(t.id); } : undefined}
                    onDragLeave={canDrag ? () => setOverTeam(null) : undefined}
                    onDrop={canDrag ? (e) => { e.preventDefault(); setOverTeam(null); if (dragId) moveEmployee(dragId, t, d.id); setDragId(null); } : undefined}
                    className={`border transition-colors ${overTeam === t.id ? "border-[#FF5500] ring-2 ring-[#FF5500]/30" : "border-[#E5E5E5]"}`}
                    data-testid={`team-${t.id}`}>
                    <div className="px-4 py-2.5 bg-[#F7F7F5] border-b border-[#E5E5E5] flex items-center gap-2">
                      <TreeStructure size={16} className="text-[#FF5500]" />
                      <span className="font-semibold text-sm">{t.name}</span>
                      <span className="ml-auto text-xs text-neutral-500">{t.employees.length} member(s)</span>
                    </div>
                    <div className="px-4 py-2 border-b border-[#F0F0EE] text-sm">
                      <span className="eyebrow text-neutral-400 mr-2">Admin</span>
                      {t.admin ? <span className="font-medium text-[#1F4E8C]">{t.admin.name}</span> : <span className="text-neutral-400">Unassigned</span>}
                    </div>
                    <ul className="px-4 py-2 space-y-1 max-h-48 overflow-y-auto">
                      {t.employees.map((e) => (
                        <li key={e.id}
                          draggable={canDrag}
                          onDragStart={canDrag ? () => setDragId(e.id) : undefined}
                          onDragEnd={canDrag ? () => setDragId(null) : undefined}
                          className={`text-sm flex items-center gap-2 ${canDrag ? "cursor-grab hover:bg-[#F7F7F5] px-1 -mx-1" : ""} ${dragId === e.id ? "opacity-40" : ""}`}
                          data-testid={`emp-${e.id}`}>
                          <span className="w-1.5 h-1.5 bg-neutral-300 rounded-full" />
                          {e.name}
                          <span className="text-xs text-neutral-400">{e.designation}</span>
                          {e.status !== "active" && <span className="text-[10px] text-red-500 uppercase">({e.status})</span>}
                        </li>
                      ))}
                      {t.employees.length === 0 && <li className="text-xs text-neutral-400">No employees</li>}
                    </ul>
                  </div>
                ))}
              </div>
            )}
          </div>
        ))}
      </div>
      </>)}
    </div>
  );
}

function OrgChart({ data, company }) {
  const [exporting, setExporting] = useState(false);

  const exportPDF = async () => {
    const el = document.getElementById("org-chart-capture");
    if (!el) return;
    setExporting(true);
    try {
      const canvas = await html2canvas(el, { scale: 1.5, backgroundColor: "#FFFFFF", useCORS: true });
      const img = canvas.toDataURL("image/png");
      const orientation = canvas.width >= canvas.height ? "landscape" : "portrait";
      const pdf = new jsPDF({ orientation, unit: "pt", format: "a4" });
      const pw = pdf.internal.pageSize.getWidth();
      const ph = pdf.internal.pageSize.getHeight();
      const margin = 32;
      const headerH = 60;
      const [sr, sg, sb] = hexToRgb(company?.brand?.secondaryColor || "#1F4E8C");
      const [pr, pg, pb] = hexToRgb(company?.brand?.primaryColor || "#FF5500");
      const logo = await loadDataUrl(company?.brand?.logo);
      const compName = company?.name || company?.shortName || "Organization";

      const drawHeader = (subtitle) => {
        pdf.setFillColor(sr, sg, sb);
        pdf.rect(0, 0, pw, headerH, "F");
        pdf.setFillColor(pr, pg, pb);
        pdf.rect(0, headerH, pw, 4, "F");
        let textX = margin;
        if (logo) { try { pdf.addImage(logo, "PNG", margin, 14, 32, 32); textX = margin + 44; } catch (_) {} }
        pdf.setTextColor(255, 255, 255);
        pdf.setFont(undefined, "bold");
        pdf.setFontSize(16);
        pdf.text(compName, textX, 28);
        pdf.setFont(undefined, "normal");
        pdf.setFontSize(10);
        pdf.setTextColor(222, 226, 232);
        pdf.text(subtitle, textX, 45);
      };

      // Page 1 — chart
      drawHeader(`Organization Chart · ${new Date().toLocaleString()}`);
      const top = headerH + 24;
      const maxW = pw - margin * 2;
      const maxH = ph - top - margin;
      const ratio = Math.min(maxW / canvas.width, maxH / canvas.height);
      const w = canvas.width * ratio;
      const h = canvas.height * ratio;
      pdf.addImage(img, "PNG", margin + (maxW - w) / 2, top, w, h);

      // Page 2+ — team directory
      pdf.addPage();
      drawHeader("Team Directory");
      let y = top;
      const lh = 15;
      const ensure = (need) => {
        if (y + need > ph - margin) { pdf.addPage(); drawHeader("Team Directory (cont.)"); y = top; }
      };
      (data.departments || []).forEach((dept) => {
        (dept.teams || []).forEach((team) => {
          ensure(46);
          pdf.setFillColor(sr, sg, sb);
          pdf.rect(margin, y - 11, pw - margin * 2, 18, "F");
          pdf.setTextColor(255, 255, 255);
          pdf.setFont(undefined, "bold");
          pdf.setFontSize(11);
          pdf.text(`${team.name}${dept.name ? "  ·  " + dept.name : ""}`, margin + 6, y + 2);
          y += 20;
          pdf.setTextColor(60, 60, 60);
          pdf.setFont(undefined, "normal");
          pdf.setFontSize(9);
          pdf.text(`Admin: ${team.admin ? team.admin.name : "Unassigned"}`, margin + 6, y);
          pdf.text(`${(team.employees || []).length} employee(s)`, pw - margin - 6, y, { align: "right" });
          y += lh + 2;
          if ((team.employees || []).length === 0) {
            pdf.setTextColor(150, 150, 150);
            pdf.text("— no employees —", margin + 14, y);
            y += lh;
          } else {
            team.employees.forEach((emp, i) => {
              ensure(lh);
              pdf.setTextColor(30, 30, 30);
              pdf.setFontSize(9);
              const name = `${i + 1}. ${emp.name}${emp.status && emp.status !== "active" ? " (inactive)" : ""}`;
              pdf.text(name, margin + 14, y);
              pdf.setTextColor(120, 120, 120);
              const meta = [emp.designation, emp.email].filter(Boolean).join("  ·  ");
              if (meta) pdf.text(meta, pw / 2, y);
              y += lh;
            });
          }
          y += 10;
        });
      });

      const slug = (company?.shortName || "org").toLowerCase().replace(/\s+/g, "-");
      pdf.save(`${slug}-org-chart-${new Date().toISOString().slice(0, 10)}.pdf`);
      toast.success("Org chart exported");
    } catch (e) {
      toast.error("Export failed");
    }
    setExporting(false);
  };

  const palette = [
    { c: "#D9C2F0", r: "#EFE6FB" }, { c: "#8ED6C9", r: "#D7F1EB" },
    { c: "#B9C4F2", r: "#E3E8FB" }, { c: "#A9D5F5", r: "#DDEFFB" },
    { c: "#AEB8F0", r: "#E0E4FB" }, { c: "#F3C7D9", r: "#FBE6EE" },
  ];
  const managers = [];
  data.departments.forEach((d) => (d.teams || []).forEach((t) => managers.push({ ...t, dept: d.name })));
  const director = data.director;

  return (
    <div data-testid="org-chart">
      <div className="flex justify-end mb-3">
        <Button onClick={exportPDF} disabled={exporting} data-testid="export-org-chart-pdf"
          className="rounded-none bg-[#1F4E8C] hover:bg-[#163a68] text-white gap-2">
          <FilePdf size={18} weight="bold" /> {exporting ? "Exporting…" : "Download PDF"}
        </Button>
      </div>
      <div id="org-chart-capture" className="bg-white border border-[#E5E5E5] p-8 overflow-x-auto">
        <div className="min-w-max flex flex-col items-center">
          {/* Director */}
          {director && (
            <div className="flex items-center gap-3 px-4 py-3 shadow-sm min-w-[260px]" style={{ background: "#36C79E" }} data-testid="chart-director">
              <Avatar name={director.name} photo={director.profile_photo} size={52} className="ring-2 ring-white/50 shrink-0" />
              <div className="text-white">
                <div className="font-head font-bold text-sm leading-tight">{director.name}</div>
                <div className="text-[12px] text-white/85">{director.designation}</div>
              </div>
            </div>
          )}
          <div className="w-px h-7" style={{ background: "#2E4A86" }} />
          {/* Managers bus */}
          <div className="flex items-start gap-7 border-t-2 pt-0" style={{ borderColor: "#2E4A86" }}>
            {managers.map((t, idx) => {
              const pal = palette[idx % palette.length];
              return (
                <div key={t.id} className="flex flex-col items-center" data-testid={`chart-team-${t.id}`}>
                  <div className="w-px h-7" style={{ background: "#2E4A86" }} />
                  {/* Manager card */}
                  <div className="flex items-center gap-3 px-4 py-3 min-w-[210px] shadow-sm" style={{ background: t.admin ? pal.c : "#E5E7EB" }}>
                    <Avatar name={t.admin ? t.admin.name : "?"} photo={t.admin?.profile_photo} size={46} className="ring-2 ring-white/60 shrink-0" />
                    <div style={{ color: "#223257" }}>
                      <div className="font-head font-bold text-sm leading-tight">{t.admin ? t.admin.name : "Unassigned"}</div>
                      <div className="text-[12px] opacity-80">{t.admin?.designation || t.name}</div>
                    </div>
                  </div>
                  {/* Reports */}
                  {t.employees.length > 0 && (
                    <div className="mt-4 self-start ml-6 border-l-2 pl-0" style={{ borderColor: "#2E4A86" }}>
                      {t.employees.slice(0, 10).map((e) => (
                        <div key={e.id} className="relative ml-5 mb-3">
                          <span className="absolute top-5 w-5 h-px" style={{ left: "-20px", background: "#2E4A86" }} />
                          <div className="px-4 py-2.5 min-w-[180px]" style={{ background: pal.r }}>
                            <div className="font-semibold text-[13px]" style={{ color: "#223257" }}>
                              {e.name}{e.status && e.status !== "active" ? " (inactive)" : ""}
                            </div>
                            <div className="text-[11px] text-neutral-500">{e.designation || "—"}</div>
                          </div>
                        </div>
                      ))}
                      {t.employees.length > 10 && (
                        <div className="relative ml-5">
                          <span className="absolute top-4 w-5 h-px" style={{ left: "-20px", background: "#2E4A86" }} />
                          <div className="px-4 py-2 min-w-[180px] text-[12px] font-semibold" style={{ color: "#223257", background: pal.r }}>
                            +{t.employees.length - 10} more
                          </div>
                        </div>
                      )}
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        </div>
      </div>
    </div>
  );
}
